import { HStack, IconButton } from '@chakra-ui/react';
import { BsThreeDotsVertical } from 'react-icons/bs';

import Tooltip from 'components/v4/Tooltip';
import ActionsCellWrapper from 'components/tables/AGGrid/ActionsCellWrapper';

const HEARTBEAT_WARNING_THRESHOLD_MS = 15 * 60 * 1000;
const HEARTBEAT_CRITICAL_THRESHOLD_MS = 30 * 60 * 1000;
const NEVER_ACTIVE_TIMESTAMP = '0001-01-01T00:00:00';

const HEARTBEAT_ROW_COLORS = {
    warning: { light: '#FFF7D7', dark: 'rgba(251, 211, 141, 0.32)' },
    critical: { light: '#FFE3E5', dark: 'rgba(254, 178, 178, 0.32)' },
};

/**
 * Builds an AG Grid `getRowStyle` callback that highlights an engine row once
 * its last heartbeat goes stale: an amber wash between 15-30 minutes,
 * escalating to rose past 30 minutes. Reinstates the legacy MUI Engines
 * table's red/yellow row indicator (ADO 2799) with a softer palette in place
 * of the old solid red/yellow fills.
 *
 * @param {boolean} darkMode Current dark mode state.
 * @returns {function} AG Grid `getRowStyle` callback.
 */
export const getEngineRowStyle = (darkMode) => (params) => {
    const lastActive = params.data?.lastActive;
    if (!lastActive || lastActive === NEVER_ACTIVE_TIMESTAMP) {
        return undefined;
    }

    const ageMs = Date.now() - new Date(`${lastActive}Z`).getTime();
    if (ageMs < HEARTBEAT_WARNING_THRESHOLD_MS) {
        return undefined;
    }

    const level =
        ageMs < HEARTBEAT_CRITICAL_THRESHOLD_MS ? 'warning' : 'critical';
    const colors = HEARTBEAT_ROW_COLORS[level];

    // No left accent border (3182) — it pushed the row's text to the right
    // of the unhighlighted rows.
    return {
        backgroundColor: darkMode ? colors.dark : colors.light,
    };
};

/**
 * Builds the AG Grid column definitions for the Engines table. Details gets
 * the largest share of the width; the minimum widths stay small enough that
 * the table fits without a horizontal scroll bar.
 *
 * @param {object}   params
 * @param {boolean}  params.darkMode      Current dark mode state.
 * @param {function} params.onViewEngine  Called with the row data when the view action is clicked.
 * @returns {Array} AG Grid column definitions.
 */
export const getEngineTableColumns = ({ darkMode, onViewEngine }) => [
    {
        headerName: 'ID',
        field: 'id',
        flex: 2,
        minWidth: 120,
    },
    {
        headerName: 'Name',
        field: 'name',
        flex: 1,
        minWidth: 110,
        sort: 'asc',
    },
    {
        headerName: 'Last Heartbeat',
        field: 'lastActive',
        flex: 1,
        minWidth: 160,
        valueFormatter: (params) =>
            params.value ? new Date(params.value).toLocaleString() : '',
    },
    {
        headerName: 'Last Process Identifier',
        field: 'lastProcessIdentifier',
        flex: 1,
        minWidth: 130,
    },
    {
        headerName: 'Last Process Type',
        field: 'lastProcessType',
        flex: 1,
        minWidth: 120,
    },
    {
        headerName: 'Details',
        field: 'details',
        flex: 3,
        minWidth: 150,
    },
    {
        headerName: 'Action',
        maxWidth: 100,
        filter: false,
        suppressMovable: true,
        lockVisible: true,
        suppressColumnsToolPanel: true,
        suppressHeaderMenuButton: true,
        suppressHeaderFilterButton: true,
        enableRowGroup: false,
        resizable: false,
        sortable: false,
        cellStyle: () => ({ display: 'flex', alignItems: 'center' }),
        lockPosition: 'right',
        cellRenderer: (params) => (
            <ActionsCellWrapper>
                <HStack
                    spacing={0}
                    justifyContent="center"
                    alignItems="center"
                    width="100%"
                    height="100%">
                    <Tooltip label="View Engine">
                        <IconButton
                            icon={
                                <BsThreeDotsVertical
                                    fill={darkMode ? 'white' : 'black'}
                                />
                            }
                            aria-label="View"
                            variant="ghost"
                            onClick={() => {
                                onViewEngine(params.data);
                            }}
                            _hover={{
                                bg: darkMode
                                    ? 'whiteAlpha.200'
                                    : 'blackAlpha.100',
                            }}
                            _active={{
                                bg: darkMode
                                    ? 'whiteAlpha.300'
                                    : 'blackAlpha.200',
                            }}
                            _focus={{ boxShadow: 'none' }}
                        />
                    </Tooltip>
                </HStack>
            </ActionsCellWrapper>
        ),
    },
];
